import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, State, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { RiderProfileErrorConstants, RideStatusConstants, RideStatusOrder } from '@uzurv/rideWss/app/constants';
import { ApplicationException } from '@uzurv/rideWss/app/exceptions';
import {
  AuthenticatedRiderService,
  ErrorTranslationService,
  LoadingDialogService,
  MapsService,
  ModalService,
  TelephonyService
} from '@uzurv/rideWss/app/services';
import * as RiderAuthenticationActions from '@uzurv/rideWss/app/store/rider-authentication/rider-authentication.action';
import { DateUtil, isNullOrUndefined, RideUtil } from '@uzurv/rideWss/app/util';
import {
  CancellationReasonConstants,
  CreateRideSubscriptionRequest,
  FavoriteLocation,
  Itinerary,
  ItinerarySanitizer,
  LocationErrorConstants,
  OrganizationRiderListItem,
  OrganizationRiderServiceLevel,
  OrganizationServiceLevel,
  PaymentAccount,
  PaymentCollectionConstants,
  RideErrorConstants,
  RiderEligibilityProofRequest,
  RideRequest,
  RideRequestInfo,
  RideRequestTypeConstants,
  RiderService,
  ServiceLevelConstants,
  ServiceLevelType,
  TripDetail
} from '@uzurv/rideWss/clara';
import { cloneDeep } from 'lodash';
import moment from 'moment-timezone';
import { finalize, firstValueFrom, forkJoin, from, interval, of } from 'rxjs';
import { catchError, concatMap, debounceTime, filter, map, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { APP_CONFIGURATION, AppConfiguration } from '../../../../app/app-configuration';
import { PaymentState } from '../payment/payment.state';
import * as PaymentSelectors from '../payment/payment.selectors';
import * as RiderAuthenticationSelectors from '../rider-authentication/rider-authentication.selector';
import * as RidesActions from './rides.actions';
import { RidesState } from './rides.reducer';
import  { ridesState } from './rides.selectors';

import * as RidesSelectors from './rides.selectors';

@Injectable()
export class RidesEffects {
  readonly appConfiguration: AppConfiguration = inject(APP_CONFIGURATION);

  constructor(
    private actions$: Actions,
    private authenticatedRiderService: AuthenticatedRiderService,
    private errorTranslationService: ErrorTranslationService,
    private itinerarySanitizer: ItinerarySanitizer,
    private store$: Store<State<RidesState>>,
    private paymentStore$: Store<State<PaymentState>>,
    private riderService: RiderService,
    private translateService: TranslateService,
    private modalService: ModalService,
    private mapsService: MapsService,
    private loadingDialogService: LoadingDialogService,
    private telephonyService: TelephonyService
  ) {}

  refreshStaleRideData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.refreshStaleRideData),
      withLatestFrom(this.store$.select(ridesState)),
      filter(([action, state]) => this.authenticatedRiderService.isLoggedIn()),
      switchMap(([action, state]) => {
        const upcomingRidesPageSize: number = (state.upcomingRideListItemsPageNumber + 1) * state.upcomingRideListItemsPageSize;
        const pastRidesPageSize: number = (state.pastRideListItemsPageNumber + 1) * state.pastRideListItemsPageSize;
        const actions: Array<Action> = [
          RidesActions.fetchUpcomingRideListItems({ pageNumber: 0, pageSize: upcomingRidesPageSize }),
          RidesActions.fetchPastRideListItems({ pageNumber: 0, pageSize: pastRidesPageSize }),
          RidesActions.fetchOrganizationRiderListItems()
        ];

        if (!isNullOrUndefined(state.selectedRide)) {
          actions.push(RidesActions.fetchRideDetail({ rideRequestId: state.selectedRide.rideRequestId }));
        }

        return actions;
      })
    );
  });

  fetchOrganizationRiderListItems$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        RidesActions.fetchOrganizationRiderListItems,
        RiderAuthenticationActions.riderAuthenticationComplete,
        RidesActions.cancelRideSuccess,
        RidesActions.createRideSubscriptionSuccess,
        RidesActions.submitItinerarySuccess,
        RidesActions.addRideRequestToItinerarySuccess
      ),
      withLatestFrom(this.store$.select(ridesState)),
      filter(() => this.authenticatedRiderService.isLoggedIn()),
      switchMap(([action, state]) => {
        return this.riderService.getOrganizationRidersByRiderId(this.authenticatedRiderService.getLoggedInRiderId()).pipe(
          map(organizationRiderListItems => {
            return RidesActions.fetchOrganizationRiderListItemsSuccess({ organizationRiderListItems: organizationRiderListItems });
          }),
          catchError(err => of(RidesActions.fetchOrganizationRiderListItemsFail({ error: err })))
        );
      })
    );
  });

  pollOrganizationRiderListItems$ = createEffect(() =>
    interval(5 * 60 * 1000).pipe(
      filter(() => this.authenticatedRiderService.isLoggedIn()),
      switchMap(() => {
        return this.riderService.getOrganizationRidersByRiderId(this.authenticatedRiderService.getLoggedInRiderId()).pipe(
          map(organizationRiderListItems => {
            return RidesActions.fetchOrganizationRiderListItemsSuccess({ organizationRiderListItems: organizationRiderListItems });
          }),
          catchError(err => of(RidesActions.fetchOrganizationRiderListItemsFail({ error: err })))
        );
      })
    )
  );

  fetchOrganizationRiderServiceLevels$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        RidesActions.fetchOrganizationRiderServiceLevels,
        RidesActions.addOrganizationRiderServiceLevelSuccess,
        RidesActions.deleteOrganizationRiderServiceLevelSuccess,
        RidesActions.selectOrganizationRiderListItem
      ),
      withLatestFrom(this.store$.select(ridesState)),
      filter(([action, state]) => !isNullOrUndefined(state.selectedOrganizationRiderListItem)),
      switchMap(([action, state]) => {
        return this.riderService
          .getOrganizationRiderServiceLevels(
            this.authenticatedRiderService.getLoggedInRiderId(),
            state.selectedOrganizationRiderListItem.organizationRiderId
          )
          .pipe(
            map(organizationRiderServiceLevels => {
              return RidesActions.fetchOrganizationRiderServiceLevelsSuccess({
                organizationRiderServiceLevels: organizationRiderServiceLevels
              });
            }),
            catchError(err => of(RidesActions.fetchOrganizationRiderServiceLevelsFail({ error: err })))
          );
      })
    );
  });

  addOrganizationRiderServiceLevel$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.addOrganizationRiderServiceLevel),
      withLatestFrom(this.store$.select(ridesState)),
      filter(([action, state]) => !isNullOrUndefined(state.selectedOrganizationRiderListItem)),
      switchMap(([action, state]) => {
        return from(this.loadingDialogService.presentLoadingDialog(this.translateService.instant('dialog.saving'))).pipe(
          switchMap(() => {
            const organizationRiderListItem = state.selectedOrganizationRiderListItem;
            const organizationServiceLevel: OrganizationServiceLevel =
              state.selectedOrganizationRiderListItem.organizationServiceLevels.find(osl => {
                return osl.serviceLevel.code === action.serviceLevelCode;
              });

            const organizationRiderServiceLevel = new OrganizationRiderServiceLevel();
            organizationRiderServiceLevel.organizationRiderId = organizationRiderListItem.organizationRiderId;
            organizationRiderServiceLevel.serviceLevel = organizationServiceLevel.serviceLevel;
            organizationRiderServiceLevel.enabled = true;

            return this.riderService
              .addOrganizationRiderServiceLevel(
                this.authenticatedRiderService.getLoggedInRiderId(),
                organizationRiderListItem.organizationRiderId,
                organizationRiderServiceLevel
              )
              .pipe(
                map(organizationRiderServiceLevel => {
                  this.loadingDialogService.dismissLoadingDialog();
                  return RidesActions.addOrganizationRiderServiceLevelSuccess({
                    organizationRiderServiceLevel: organizationRiderServiceLevel
                  });
                }),
                catchError(err => of(RidesActions.addOrganizationRiderServiceLevelFail({ error: err })))
              );
          })
        );
      })
    );
  });

  deleteOrganizationRiderServiceLevel$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.deleteOrganizationRiderServiceLevel),
      withLatestFrom(this.store$.select(ridesState)),
      filter(([action, state]) => !isNullOrUndefined(state.selectedOrganizationRiderListItem)),
      switchMap(([action, state]) => {
        return from(this.loadingDialogService.presentLoadingDialog(this.translateService.instant('dialog.saving'))).pipe(
          switchMap(() => {
            const organizationRiderListItem = state.selectedOrganizationRiderListItem;
            const organizationServiceLevel: OrganizationServiceLevel =
              state.selectedOrganizationRiderListItem.organizationServiceLevels.find(osl => {
                return osl.serviceLevel.code === action.serviceLevelCode;
              });

            const organizationRiderServiceLevel = new OrganizationRiderServiceLevel();
            organizationRiderServiceLevel.organizationRiderId = organizationRiderListItem.organizationRiderId;
            organizationRiderServiceLevel.serviceLevel = organizationServiceLevel.serviceLevel;
            organizationRiderServiceLevel.enabled = true;

            return this.riderService
              .disableOrganizationRiderServiceLevel(
                this.authenticatedRiderService.getLoggedInRiderId(),
                organizationRiderListItem.organizationRiderId,
                organizationServiceLevel.serviceLevel.id
              )
              .pipe(
                map(success => {
                  this.loadingDialogService.dismissLoadingDialog();
                  return RidesActions.deleteOrganizationRiderServiceLevelSuccess({ success: success });
                }),
                catchError(err => of(RidesActions.deleteOrganizationRiderServiceLevelFail({ error: err })))
              );
          })
        );
      })
    );
  });

  // TODO: This is future upcoming rides list handling. Overwritable pageNumber and pageSize

  fetchUpcomingRideListItemsFuture$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.fetchUpcomingRideListItems),
      withLatestFrom(this.store$.select(ridesState)),
      switchMap(([action, state]) => {
        let pageNumber: number = state.upcomingRideListItemsPageNumber;
        let pageSize: number = state.upcomingRideListItemsPageSize;

        if (!isNullOrUndefined(action.pageNumber)) {
          pageNumber = action.pageNumber;
        }

        if (!isNullOrUndefined(action.pageSize)) {
          pageSize = action.pageSize;
        }

        return this.riderService
          .getTripListItemsByRiderId(this.authenticatedRiderService.getLoggedInRiderId(), pageNumber, pageSize, true)
          .pipe(
            map(upcomingRideListItems => {
              return RidesActions.fetchUpcomingRideListItemsSuccessFuture({ tripListItems: upcomingRideListItems });
            }),
            catchError(err => of(RidesActions.fetchUpcomingRideListItemsFail({ error: err })))
          );
      })
    );
  });

  fetchInitialRideListItems$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.fetchInitialUpcomingRideListItems),
      withLatestFrom(this.store$.select(ridesState)),
      filter(([action, state]) => {
        return !state.fetchingUpcomingRideListItems;
      }),
      switchMap(([action, state]) => {
        return this.riderService
          .getTripListItemsByRiderId(this.authenticatedRiderService.getLoggedInRiderId(), 0, state.upcomingRideListItemsPageSize, true)
          .pipe(
            map(upcomingRideListItems => RidesActions.fetchUpcomingRideListItemsSuccess({ tripListItems: upcomingRideListItems })),
            catchError(err => of(RidesActions.fetchUpcomingRideListItemsFail({ error: err })))
          );
      })
    );
  });

  fetchUpcomingRideListItems$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.refreshUpcomingRideListItems, RidesActions.fetchAdditionalUpcomingRideListItems),
      withLatestFrom(this.store$.select(ridesState)),
      filter(([action, state]) => !state.fetchingUpcomingRideListItems),
      switchMap(([action, state]) => {
        return this.riderService
          .getTripListItemsByRiderId(
            this.authenticatedRiderService.getLoggedInRiderId(),
            state.upcomingRideListItemsPageNumber,
            state.upcomingRideListItemsPageSize,
            true
          )
          .pipe(
            map(upcomingRideListItems => {
              if (action.type === RidesActions.refreshUpcomingRideListItems.type) {
                return RidesActions.refreshUpcomingRideListItemsSuccess({ tripListItems: upcomingRideListItems });
              } else {
                return RidesActions.fetchUpcomingRideListItemsSuccess({ tripListItems: upcomingRideListItems });
              }
            }),
            catchError(err => of(RidesActions.fetchUpcomingRideListItemsFail({ error: err })))
          );
      })
    );
  });

  // TODO: This is future past rides list handling. Overwritable pageNumber and pageSize
  fetchPastRideListItemsFuture$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.fetchPastRideListItems),
      withLatestFrom(this.store$.select(ridesState)),
      switchMap(([action, state]) => {
        let pageNumber: number = state.pastRideListItemsPageNumber;
        let pageSize: number = state.pastRideListItemsPageSize;

        if (!isNullOrUndefined(action.pageNumber)) {
          pageNumber = action.pageNumber;
        }

        if (!isNullOrUndefined(action.pageSize)) {
          pageSize = action.pageSize;
        }

        return this.riderService
          .getTripListItemsByRiderId(this.authenticatedRiderService.getLoggedInRiderId(), pageNumber, pageSize, false)
          .pipe(
            map(pastRideListItems => {
              return RidesActions.fetchPastRideListItemsSuccessFuture({ tripListItems: pastRideListItems });
            }),
            catchError(err => of(RidesActions.fetchPastRideListItemsFail({ error: err })))
          );
      })
    );
  });

  fetchInitialPastRideListItems$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.fetchInitialPastRideListItems),
      withLatestFrom(this.store$.select(ridesState)),
      filter(([action, state]) => !state.fetchingPastRideListItems),
      switchMap(([action, state]) => {
        return this.riderService
          .getTripListItemsByRiderId(this.authenticatedRiderService.getLoggedInRiderId(), 0, state.pastRideListItemsPageSize, false)
          .pipe(
            map(pastRideListItems => {
              return RidesActions.fetchPastRideListItemsSuccess({ tripListItems: pastRideListItems });
            }),
            catchError(err => of(RidesActions.fetchPastRideListItemsFail({ error: err })))
          );
      })
    );
  });

  fetchPastRideListItems$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.refreshPastRideListItems, RidesActions.fetchAdditionalPastRideListItems),
      withLatestFrom(this.store$.select(ridesState)),
      filter(([action, state]) => !state.fetchingPastRideListItems),
      switchMap(([action, state]) => {
        return this.riderService
          .getTripListItemsByRiderId(
            this.authenticatedRiderService.getLoggedInRiderId(),
            state.pastRideListItemsPageNumber,
            state.pastRideListItemsPageSize,
            false
          )
          .pipe(
            map(pastRideListItems => {
              if (action.type === RidesActions.refreshPastRideListItems.type) {
                return RidesActions.refreshPastRideListItemsSuccess({ tripListItems: pastRideListItems });
              } else {
                return RidesActions.fetchPastRideListItemsSuccess({ tripListItems: pastRideListItems });
              }
            }),
            catchError(err => of(RidesActions.fetchPastRideListItemsFail({ error: err })))
          );
      })
    );
  });

  rideListItemSelected$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RidesActions.rideSelected),
        withLatestFrom(this.store$.select(ridesState)),
        map(([action, state]) => {
          this.store$.dispatch(RidesActions.fetchRideDetail({ rideRequestId: action.rideRequestId, setSelectedRide: true }));
        })
      );
    },
    { dispatch: false }
  );

  fetchRideDetail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.fetchRideDetail),
      mergeMap(action => {
        return this.riderService
          .getTripDetailByRideRequestId(this.authenticatedRiderService.getLoggedInRiderId(), action.rideRequestId)
          .pipe(
            map(rideDetail => {
              return RidesActions.fetchRideDetailSuccess({ tripDetail: rideDetail, setSelectedRide: action.setSelectedRide });
            }),
            catchError(err => of(RidesActions.fetchRideDetailFail({ error: err })))
          );
      })
    );
  });

  refreshRideDetail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.refreshRideDetail),
      switchMap(action => {
        return this.riderService
          .getTripDetailByRideRequestId(this.authenticatedRiderService.getLoggedInRiderId(), action.rideRequestId)
          .pipe(
            map(rideDetail => {
              return RidesActions.fetchRideDetailSuccess({ tripDetail: rideDetail });
            }),
            catchError(err => of(RidesActions.fetchRideDetailFail({ error: err })))
          );
      })
    );
  });

  updateRiderPosition$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RidesActions.updateRiderPosition),
        withLatestFrom(this.store$.select(ridesState)),
        filter(([action, state]) =>
          state.upcomingRideListItems.some(upcomingRide => upcomingRide.status === RideStatusConstants.PICKUP_ARRIVED)
        ),
        map(async ([action, state]) => {
          try {
            await firstValueFrom(
              this.riderService.updateRiderPosition(
                this.authenticatedRiderService.getLoggedInRiderId(),
                action.geoLocationUpdate.latitude,
                action.geoLocationUpdate.longitude,
                action.geoLocationUpdate.timestamp
              )
            );
          } catch (error) {
            // Do nothing
          }
        })
      );
    },
    { dispatch: false }
  );

  fetchInProgressRideData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.fetchRideDetailSuccess),
      filter(
        action =>
          RideStatusOrder[action.tripDetail.status] > RideStatusOrder[RideStatusConstants.SCHEDULED] &&
          RideStatusOrder[action.tripDetail.status] < RideStatusOrder[RideStatusConstants.COMPLETED]
      ),
      map(action => RidesActions.fetchRideEstimates({ rideRequestId: action.tripDetail.rideRequestId }))
    );
  });

  fetchRideEstimates$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.fetchRideEstimates),
      switchMap(action => {
        return this.riderService
          .getRideEstimatesByRideRequestId(this.authenticatedRiderService.getLoggedInRiderId(), action.rideRequestId)
          .pipe(
            map(rideEstimate => {
              return RidesActions.fetchRideEstimatesSuccess({ rideEstimate: rideEstimate });
            }),
            catchError(err => of(RidesActions.fetchRideEstimatesFail({ error: err })))
          );
      })
    );
  });

  activateWillCallRideFromList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.activateWillCallRideFromList),
      switchMap(action =>
        forkJoin([
          this.loadingDialogService.presentLoadingDialog(this.translateService.instant('dialog.loading.activatingWillCall')),
          of(action)
        ])
      ),
      switchMap(([_, action]) =>
        this.riderService.activateWillCallRide(this.authenticatedRiderService.getLoggedInRiderId(), action.rideRequestId).pipe(
          tap(() => this.loadingDialogService.dismissLoadingDialog()),
          map(result => RidesActions.activateWillCallRideSuccess({ tripDetail: result })),
          catchError(err => {
            return of(RidesActions.activateWillCallRideFail({ rideRequestId: action.rideRequestId, error: err }));
          })
        )
      )
    );
  });

  activateWillCallRideFromDetail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.activateWillCallRideFromDetail),
      switchMap(action => {
        return this.riderService.activateWillCallRide(this.authenticatedRiderService.getLoggedInRiderId(), action.rideRequestId).pipe(
          tap(() => this.loadingDialogService.dismissLoadingDialog()),
          map(rideDetail => {
            return RidesActions.activateWillCallRideSuccess({ tripDetail: rideDetail });
          }),
          catchError(err => of(RidesActions.activateWillCallRideFail({ rideRequestId: action.rideRequestId, error: err })))
        );
      })
    );
  });

  cancelRide$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RidesActions.cancelRide),
        debounceTime(250),
        switchMap(action => {
          return this.riderService
            .getTripDetailByRideRequestId(this.authenticatedRiderService.getLoggedInRiderId(), action.rideRequestId)
            .pipe(
              map(async rideDetail => {
                if (rideDetail.transitProgramInfo.organization.riderAppItineraryCreationEnabled) {
                  if (rideDetail.rideRequestType.onDemand) {
                    if (isNullOrUndefined(rideDetail.reservationId)) {
                      await this.displayCancelRideAlert(rideDetail);
                    } else {
                      await this.displayCallUzurvToCancelRideAlert(
                        rideDetail.transitProgramInfo.riderCallCenterPhoneNumber,
                        this.translateService.instant('rideDetails.error.message.cannotCancelPairedOnDemandRide')
                      );
                    }
                  } else {
                    if (rideDetail.status === RideStatusConstants.SCHEDULED || rideDetail.status === RideStatusConstants.PENDING) {
                      await this.displayCancelRideAlert(rideDetail);
                    } else {
                      await this.displayCallUzurvToCancelRideAlert(
                        rideDetail.transitProgramInfo.riderCallCenterPhoneNumber,
                        this.translateService.instant('rideDetails.alert.message.cannotCancelRideOutsideOfScheduledStatus')
                      );
                    }
                  }
                } else {
                  await this.displayCallOrganizationToCancelRideAlert(
                    rideDetail.transitProgramInfo.organization.name,
                    rideDetail.transitProgramInfo.organization.bookingPhoneNumber
                  );
                }
              }),
              catchError(err =>
                of(RidesActions.cancelRideFail({ riderCallCenterPhoneNumber: this.appConfiguration.supportPhoneNumber, error: err }))
              )
            );
        })
      );
    },
    { dispatch: false }
  );

  cancelRideConfirmed$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.cancelRideConfirmed),
      switchMap(action => {
        return this.riderService.findCancellationReasonWithName(CancellationReasonConstants.RIDER_CANCELLED).pipe(
          switchMap(cancellationReason => {
            if (!isNullOrUndefined(action.tripDetail.reservationId)) {
              return this.riderService
                .cancelReservationWithCancellationReason(
                  this.authenticatedRiderService.getLoggedInRiderId(),
                  action.tripDetail.reservationId,
                  cancellationReason
                )
                .pipe(
                  switchMap(rideDetail => {
                    return [RidesActions.cancelRideSuccess({ tripDetail: rideDetail })];
                  }),
                  catchError(err =>
                    of(
                      RidesActions.cancelRideFail({
                        riderCallCenterPhoneNumber: action.tripDetail.transitProgramInfo.riderCallCenterPhoneNumber,
                        error: err
                      })
                    )
                  )
                );
            } else {
              return this.riderService
                .cancelRideRequestWithCancellationReason(
                  this.authenticatedRiderService.getLoggedInRiderId(),
                  action.tripDetail.rideRequestId,
                  cancellationReason.id
                )
                .pipe(
                  map(rideDetail => {
                    return RidesActions.cancelRideSuccess({ tripDetail: rideDetail });
                  }),
                  catchError(err =>
                    of(
                      RidesActions.cancelRideFail({
                        riderCallCenterPhoneNumber: action.tripDetail.transitProgramInfo.riderCallCenterPhoneNumber,
                        error: err
                      })
                    )
                  )
                );
            }
          })
        );
      })
    );
  });

  fetchFavoriteLocations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.fetchFavoriteLocations),
      withLatestFrom(this.store$.select(ridesState)),
      switchMap(([action, state]) => {
        return this.riderService.listFavoriteLocationsByRiderId(this.authenticatedRiderService.getLoggedInRiderId()).pipe(
          map(favoriteLocations => {
            return RidesActions.fetchFavoriteLocationsSuccess({ favoriteLocations: favoriteLocations });
          }),
          catchError(err => of(RidesActions.fetchFavoriteLocationsFail({ error: err })))
        );
      })
    );
  });

  replaceFavoriteLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.replaceFavoriteLocation),
      withLatestFrom(this.store$.select(ridesState)),
      switchMap(([action]) => {
        this.loadingDialogService.presentLoadingDialog(this.translateService.instant('dialog.loading.addingFavoriteLocation'));
        return this.riderService
          .deleteFavoriteLocation(this.authenticatedRiderService.getLoggedInRiderId(), action.toDelete.location.id)
          .pipe(
            switchMap(success => {
              this.store$.dispatch(RidesActions.deleteFavoriteLocationSuccess({ favoriteLocation: action.toDelete }));

              return this.riderService
                .addFavoriteLocationForRiderWithLabel(
                  this.authenticatedRiderService.getLoggedInRiderId(),
                  action.toCreate.location,
                  action.toCreate.label
                )
                .pipe(
                  map(favoriteLocation => {
                    this.loadingDialogService.dismissLoadingDialog();
                    return RidesActions.addFavoriteLocationSuccess({ favoriteLocation: favoriteLocation });
                  }),
                  catchError(error => of(RidesActions.addFavoriteLocationFail({ favoriteLocation: action.toCreate, error: error })))
                );
            }),
            catchError(error => of(RidesActions.deleteFavoriteLocationFail({ favoriteLocation: null, error: error })))
          );
      })
    );
  });

  addFavoriteLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.addFavoriteLocation),
      withLatestFrom(this.store$.select(ridesState)),
      switchMap(([action]) => {
        this.loadingDialogService.presentLoadingDialog(this.translateService.instant('dialog.loading.addingFavoriteLocation'));
        return this.riderService
          .addFavoriteLocationForRiderWithLabel(
            this.authenticatedRiderService.getLoggedInRiderId(),
            action.favoriteLocation.location,
            action.favoriteLocation.label
          )
          .pipe(
            map(favoriteLocation => {
              this.loadingDialogService.dismissLoadingDialog();
              return RidesActions.addFavoriteLocationSuccess({ favoriteLocation: favoriteLocation });
            }),
            catchError(error => of(RidesActions.addFavoriteLocationFail({ favoriteLocation: action.favoriteLocation, error: error })))
          );
      })
    );
  });

  deleteFavoriteLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.deleteFavoriteLocation),
      withLatestFrom(this.store$.select(ridesState)),
      switchMap(([action]) => {
        return this.riderService
          .deleteFavoriteLocation(this.authenticatedRiderService.getLoggedInRiderId(), action.favoriteLocation.location.id)
          .pipe(
            map(success => {
              return RidesActions.deleteFavoriteLocationSuccess({ favoriteLocation: action.favoriteLocation });
            }),
            catchError(error => of(RidesActions.deleteFavoriteLocationFail({ favoriteLocation: action.favoriteLocation, error: error })))
          );
      })
    );
  });

  fetchRecentLocations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.fetchRecentLocations),
      withLatestFrom(this.store$.select(ridesState)),
      switchMap(([action, state]) => {
        return this.riderService.listRecentLocationsByRiderId(this.authenticatedRiderService.getLoggedInRiderId(), 0, 10).pipe(
          map(recentLocations => {
            return RidesActions.fetchRecentLocationsSuccess({ locations: recentLocations });
          }),
          catchError(err => of(RidesActions.fetchRecentLocationsFail({ error: err })))
        );
      })
    );
  });

  fetchRideRequestTypes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.fetchRideRequestTypes),
      withLatestFrom(this.store$.select(ridesState)),
      switchMap(([action, state]) => {
        return this.riderService.listRideRequestTypes().pipe(
          map(rideRequestTypes => {
            return RidesActions.fetchRideRequestTypesSuccess({ rideRequestTypes: rideRequestTypes });
          }),
          catchError(err => of(RidesActions.fetchRideRequestTypesFail({ error: err })))
        );
      })
    );
  });

  /**
   * if the selected organization service level has either scheduled or on-demand ride request type unavailable,
   * set the ride request type to the available one. If both are available, do not change the selection and let the rider choose
   */
  setRideRequestType$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RidesActions.selectServiceLevelType),
        withLatestFrom(this.store$.select(RidesSelectors.isRideRequestTypeSelectionUnavailable), this.store$.select(RidesSelectors.ridesState)),
        map(([_, isUnavailable, state]) => {
          if (isUnavailable) {
            const selectedServiceLevelType = state.selectedServiceLevelType;
            const selectedOrganizationRiderListItem = state.selectedOrganizationRiderListItem;

            if (!isNullOrUndefined(selectedServiceLevelType) && !isNullOrUndefined(selectedOrganizationRiderListItem)) {
              const selectedOrganizationServiceLevel = state.selectedOrganizationRiderListItem?.organizationServiceLevels.find(
                serviceLevel => serviceLevel.serviceLevel.id === state.currentRideRequestLeg?.serviceLevel?.id
              );

              if (selectedOrganizationServiceLevel) {
                if (selectedOrganizationServiceLevel.scheduledEnabled || selectedOrganizationServiceLevel.willCallEnabled) {
                  this.store$.dispatch(RidesActions.selectRideRequestType({ code: RideRequestTypeConstants.SCHEDULED }));
                } else if (selectedOrganizationServiceLevel.rideNowEnabled) {
                  this.store$.dispatch(RidesActions.selectRideRequestType({ code: RideRequestTypeConstants.RIDE_NOW }));
                }
              }
            }
          } else {
            this.store$.dispatch(RidesActions.selectRideRequestType({ code: null }));
          }
          return of();
        })
      )
    },
    { dispatch: false }
  );

  setDefaultServiceLevel$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RidesActions.startNewItinerary, RidesActions.fetchOrganizationRiderServiceLevelsSuccess),
        withLatestFrom(this.store$.select(ridesState), this.store$.select(RiderAuthenticationSelectors.getMobilityProfileDetail)),
        filter(
          ([_, state, mobilityProfileDetail]) =>
            !isNullOrUndefined(state.itinerary) && !isNullOrUndefined(state.selectedOrganizationRiderListItem?.organizationServiceLevels)
        ),
        map(([_, state, mobilityProfileDetail]) => {
          // OrganizationServiceLevel logic
          const hasAmbulatory: boolean = state.selectedOrganizationRiderListItem.organizationServiceLevels.some(osl =>
            osl.serviceLevel.isAmbulatory()
          );

          const hasNonAmbulatory: boolean = state.selectedOrganizationRiderListItem.organizationServiceLevels.some(osl =>
            osl.serviceLevel.isNonAmbulatory()
          );

          if (hasAmbulatory && !hasNonAmbulatory) {
            this.store$.dispatch(RidesActions.selectServiceLevelType({ serviceLevelType: ServiceLevelType.AMBULATORY }));
          } else if (!hasAmbulatory && hasNonAmbulatory) {
            this.store$.dispatch(RidesActions.selectServiceLevelType({ serviceLevelType: ServiceLevelType.NON_AMBULATORY }));
          }

          // Check if the Org has a NonAmbulatory, if so, check if MobilityProfile exist
          if (state.organizationRiderServiceLevels.some(orsl => orsl.serviceLevel.isNonAmbulatory())) {
            if (!isNullOrUndefined(mobilityProfileDetail)) {
              const mobilityProfileRecommended =
                mobilityProfileDetail && mobilityProfileDetail.creationNeeded === false
                  ? mobilityProfileDetail.recommendedServiceLevel
                  : null;

              if (mobilityProfileRecommended) {
                this.store$.dispatch(
                  RidesActions.selectServiceLevelType({
                    serviceLevelType: mobilityProfileRecommended.isNonAmbulatory()
                      ? ServiceLevelType.NON_AMBULATORY
                      : ServiceLevelType.AMBULATORY
                  })
                );
                return;
              }
            }
          }
        })
      );
    },
    { dispatch: false }
  );

  setRideBackupAccepted$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RidesActions.setRideBackupAccepted),
        withLatestFrom(this.store$.select(ridesState)),
        map(([_, state]) => {
          // Reset payment method if fare collection is turned on
          if (
            state.selectedPaymentCollectionType?.code === PaymentCollectionConstants.FARE_COLLECTION &&
            state.currentRideRequestLeg?.rideBackupAccepted
          ) {
            // Clear payment info
            this.store$.dispatch(
              RidesActions.setPaymentCollectionDetailsForNewItinerarySuccess({ paymentCollectionTypeCode: null, paymentAccount: null })
            );

            // Prompt
            this.store$.dispatch(
              RidesActions.setPaymentCollectionDetailsForNewItineraryFail({
                error: new ApplicationException(
                  this.translateService.instant('rideBackupPreference.error.rideBackupNotAvailableWithFareCollection.message')
                )
              })
            );
          }
        })
      );
    },
    { dispatch: false }
  );

  fetchAutocompleteResults$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.fetchAutocompleteResults),
      withLatestFrom(this.store$.select(ridesState)),
      switchMap(([action, state]) => {
        return this.mapsService.autocomplete(action.autocompleteInputValue).pipe(
          map(autocompleteResults => {
            return RidesActions.fetchAutocompleteResultsSuccess({
              autoCompleteResults: autocompleteResults,
              locationType: action.locationType
            });
          }),
          catchError(err => of(RidesActions.fetchAutocompleteResultsFail({ error: err })))
        );
      })
    );
  });

  selectAutocompleteResult$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.selectLocationFromAutocompleteResult),
      switchMap(action =>
        this.mapsService.geocodeLocationByAutoCompleteResult(action.autoCompleteResult).pipe(
          map(location =>
            RidesActions.selectLocation({ rideRequestLocation: RideUtil.locationToRideRequestLocation(location), locationType: action.locationType })
          ),
          catchError(err => of(RidesActions.geocodeLocationFail({ error: err })))
        )
      ),
      catchError(err => of(RidesActions.geocodeLocationFail({ error: err })))
    );
  });

  handlePickupLocationChanged$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.selectLocation),
      filter(action => !isNullOrUndefined(action.rideRequestLocation)),
      map(action =>
        RidesActions.addLocation({
          display: action.rideRequestLocation.display,
          locationType: action.locationType
        })
      )
    );
  });

  addLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.selectLocation),
      filter(action => !isNullOrUndefined(action.rideRequestLocation)),
      withLatestFrom(this.store$.select(ridesState)),
      switchMap(([action]) => {
        return this.riderService.addLocation(action.rideRequestLocation.display).pipe(
          map(location =>
            RidesActions.addLocationSuccess({
              locationType: action.locationType,
              rideRequestLocation: RideUtil.locationToRideRequestLocation(location)
            })
          ),
          catchError(error => of(RidesActions.addLocationFail({ error: error, locationType: action.locationType })))
        );
      })
    );
  });

  fetchClosestTransitProgramOnPickupLocationChange$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.addLocationSuccess),
      withLatestFrom(this.store$.select(ridesState)),
      filter(
        ([action, state]) =>
          !isNullOrUndefined(state.currentRideRequestLeg) &&
          !isNullOrUndefined(state.currentRideRequestLeg.pickupLocation) &&
          !isNullOrUndefined(state.currentRideRequestLeg.dropoffLocation)
      ),
      map(([action, state]) =>
        RidesActions.fetchClosestTransitProgram({
          pickUpLocationId: state.currentRideRequestLeg.pickupLocation.locationId,
          dropOffLocationId: state.currentRideRequestLeg.dropoffLocation.locationId
        })
      )
    );
  });

  fetchClosestTransitProgramOnNewLeg$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.startNewItineraryLeg, RidesActions.startNewItineraryReturnTrip),
      withLatestFrom(this.store$.select(ridesState)),
      filter(
        ([action, state]) =>
          !isNullOrUndefined(state.currentRideRequestLeg) &&
          !isNullOrUndefined(state.currentRideRequestLeg.pickupLocation) &&
          !isNullOrUndefined(state.currentRideRequestLeg.dropoffLocation)
      ),
      map(([action, state]) =>
        RidesActions.fetchClosestTransitProgram({
          pickUpLocationId: state.currentRideRequestLeg.pickupLocation.locationId,
          dropOffLocationId: state.currentRideRequestLeg.dropoffLocation.locationId
        })
      )
    );
  });

  fetchClosestTransitProgram$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.fetchClosestTransitProgram),
      withLatestFrom(this.store$.select(ridesState)),
      switchMap(([action, state]) => {
        return this.riderService
          .findNearestTransitProgramByLocations(
            state.selectedOrganizationRiderListItem.organizationId,
            action.pickUpLocationId,
            action.dropOffLocationId
          )
          .pipe(
            map(transitProgram => RidesActions.fetchClosestTransitProgramSuccess({ transitProgramInfo: transitProgram })),
            catchError(err => of(RidesActions.fetchClosestTransitProgramFail({ error: err })))
          );
      })
    );
  });

  listRideMetadataFieldsForTransitProgram = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.fetchClosestTransitProgramSuccess),
      withLatestFrom(this.store$.select(ridesState)),
      switchMap(([action, state]) => {
        return this.riderService
          .listRideMetadataFieldsForTransitProgram(this.authenticatedRiderService.getLoggedInRiderId(), action.transitProgramInfo.id)
          .pipe(
            map(rideMetadataFields =>
              RidesActions.fetchRideMetadataFieldsForTransitProgramSuccess({ rideMetadataFields: rideMetadataFields })
            ),
            catchError(err => of(RidesActions.fetchRideMetadataFieldsForTransitProgramFail({ error: err })))
          );
      })
    );
  });

  buildPickupDateFromTime$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.selectCurrentLegPickupTime, RidesActions.selectItineraryDate),
      withLatestFrom(this.store$.select(ridesState)),
      switchMap(([_, state]) => {
        if (isNullOrUndefined(state.itinerary.pickupDate) || isNullOrUndefined(state.currentRideRequestLeg.localPickUpDate)) {
          return of();
        }

        return of(RidesActions.selectCurrentLegPickupDate({ date: state.itinerary.pickupDate }));
      })
    );
  });

  fetchRideRequestInfo$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        RidesActions.fetchRideRequestInfo,
        RidesActions.fetchClosestTransitProgramSuccess,
        RidesActions.selectCurrentLegPickupDate,
        RidesActions.selectPassengerCount,
        RidesActions.selectRiderHasPca,
        RidesActions.selectLocation,
        RidesActions.selectRideRequestType,
        RidesActions.selectServiceLevelType
      ),
      withLatestFrom(this.store$.select(ridesState)),
      filter(([action, state]) => {
        const rideRequest: RideRequest = state.currentRideRequestLeg;

        if (isNullOrUndefined(rideRequest)) {
          return false;
        }

        if (isNullOrUndefined(rideRequest.pickupLocation) || isNullOrUndefined(rideRequest.dropoffLocation)) {
          return false;
        }

        if (isNullOrUndefined(rideRequest.billablePassengerCount)) {
          return false;
        }

        if (isNullOrUndefined(state.closestTransitProgram)) {
          return false;
        }

        if (isNullOrUndefined(state.selectedServiceLevelType)) {
          return false;
        }

        if (!rideRequest.rideRequestType.onDemand && isNullOrUndefined(rideRequest.pickupDate)) {
          return false;
        }

        return true;
      }),
      switchMap(([action, state]) => {
        let serviceLevelId: number;

        if (state.selectedServiceLevelType === ServiceLevelType.AMBULATORY) {
          const ad2dServiceLevel = state.selectedOrganizationRiderListItem.organizationServiceLevels.find(
            osl => osl.serviceLevel.code === ServiceLevelConstants.AMBULATORY_DOOR_TO_DOOR_CODE
          );

          if (isNullOrUndefined(ad2dServiceLevel)) {
            return of(RidesActions.fetchRideRequestInfoFail({ error: new ApplicationException('Missing required service level') }));
          }

          serviceLevelId = ad2dServiceLevel.serviceLevel.id;
        } else if (state.selectedServiceLevelType === ServiceLevelType.NON_AMBULATORY) {
          const nad2dServiceLevel = state.selectedOrganizationRiderListItem.organizationServiceLevels.find(
            osl => osl.serviceLevel.code === ServiceLevelConstants.NON_AMBULATORY_DOOR_TO_DOOR_CODE
          );

          if (isNullOrUndefined(nad2dServiceLevel)) {
            return of(RidesActions.fetchRideRequestInfoFail({ error: new ApplicationException('Missing required service level') }));
          }

          serviceLevelId = nad2dServiceLevel.serviceLevel.id;
        } else {
          return of(RidesActions.fetchRideRequestInfoFail({ error: new ApplicationException('Unrecognized service level type') }));
        }

        return this.riderService
          .getTransitProgramRideRequestInfo(
            state.closestTransitProgram.id,
            state.currentRideRequestLeg.pickupLocation.latitude,
            state.currentRideRequestLeg.pickupLocation.longitude,
            state.currentRideRequestLeg.dropoffLocation.latitude,
            state.currentRideRequestLeg.dropoffLocation.longitude,
            state.currentRideRequestLeg.billablePassengerCount,
            state.currentRideRequestLeg.pickupDate,
            null,
            serviceLevelId
          )
          .pipe(
            map(rideRequestInfo => {
              return RidesActions.fetchRideRequestInfoSuccess({ rideRequestInfo: rideRequestInfo });
            }),
            catchError(err => of(RidesActions.fetchRideRequestInfoFail({ error: err })))
          );
      })
    );
  });

  // Fetches fresh rideRequestInfos for each ride request in the itinerary.
  updateRideSubscriptionDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.updateRideSubscriptionDetails),
      withLatestFrom(this.store$.select(ridesState)),
      switchMap(([action, state]) => {
        this.loadingDialogService.presentLoadingDialog(this.translateService.instant('dialog.loading.updatingSubscription'));
        const itinerary: Itinerary = cloneDeep(state.itinerary);
        const observables = [];

        itinerary.rideRequests.forEach(rideRequest => {
          observables.push(
            this.riderService.getTransitProgramRideRequestInfo(
              state.closestTransitProgram.id,
              rideRequest.pickupLocation.latitude,
              rideRequest.pickupLocation.longitude,
              rideRequest.dropoffLocation.latitude,
              rideRequest.dropoffLocation.longitude,
              rideRequest.billablePassengerCount,
              rideRequest.pickupDate,
              null,
              rideRequest.serviceLevel.id
            )
          );
        });

        return forkJoin(observables).pipe(
          map(rideRequestInfos => {
            // forkjoin returns the results in the same order as the requests.
            for (let i = 0; i < rideRequestInfos.length; i++) {
              itinerary.rideRequests[i].rideRequestInfo = rideRequestInfos[i];
              itinerary.rideRequests[i].distanceEstimate = rideRequestInfos[i].distanceEstimate;
              itinerary.rideRequests[i].durationEstimate = rideRequestInfos[i].durationEstimate;

              if (!isNullOrUndefined(rideRequestInfos[i].tolls) && isNullOrUndefined(itinerary.rideRequests[i].tollEstimate)) {
                itinerary.rideRequests[i].tollEstimate = rideRequestInfos[i].tolls;
              }

              if (!isNullOrUndefined(rideRequestInfos[i].riderFee)) {
                itinerary.rideRequests[i].requestFee = rideRequestInfos[i].riderFee;
              }

              itinerary.rideRequests[i].dropoffDate = rideRequestInfos[i].estimatedDropoffDate;
            }
            this.loadingDialogService.dismissLoadingDialog();
            return RidesActions.updateRideSubscriptionDetailsSuccess({ itinerary: itinerary });
          }),
          catchError(err => of(RidesActions.updateRideSubscriptionDetailsFail({ error: err })))
        );
      })
    );
  });

  fetchAvailableProviders$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.fetchAvailableProviders, RidesActions.fetchRideRequestInfoSuccess),
      withLatestFrom(this.store$.select(ridesState)),
      filter(([action, state]) => {
        const rideRequest: RideRequest = state.currentRideRequestLeg;

        if (isNullOrUndefined(rideRequest)) {
          return false;
        }

        if (isNullOrUndefined(state.currentRideRequestLeg.rideRequestInfo)) {
          return false;
        }

        if (!rideRequest.rideRequestType.onDemand) {
          if (isNullOrUndefined(rideRequest.pickupDate)) {
            return false;
          }

          if (!moment(rideRequest.pickupDate).isAfter(new Date())) {
            return false;
          }
        }

        if (isNullOrUndefined(state.closestTransitProgram)) {
          return false;
        }

        if (isNullOrUndefined(state.selectedServiceLevelType)) {
          return false;
        }

        if (!state.selectedOrganizationRiderListItem?.preferredProviderEnabled) {
          return false;
        }

        return true;
      }),
      switchMap(([action, state]) => {
        // Pick up Date
        let pickupDate;

        if (state.currentRideRequestLeg.rideRequestType.onDemand) {
          pickupDate = new Date();
        } else {
          if (isNullOrUndefined(state.currentRideRequestLeg.rideRequestInfo.estimatedPickupDate)) {
            pickupDate = state.currentRideRequestLeg.pickupDate;
          } else {
            pickupDate = state.currentRideRequestLeg.rideRequestInfo.estimatedPickupDate;
          }
        }

        pickupDate = DateUtil.adjustDateFromLocalToUTC(pickupDate);

        // Drop off date
        let dropoffDate;

        if (state.currentRideRequestLeg.rideRequestType.onDemand) {
          dropoffDate = moment().add(state.currentRideRequestLeg.durationEstimate, 'seconds').toDate();
        } else {
          if (isNullOrUndefined(state.currentRideRequestLeg.rideRequestInfo.estimatedDropoffDate)) {
            dropoffDate = state.currentRideRequestLeg.dropoffDate;
          } else {
            dropoffDate = state.currentRideRequestLeg.rideRequestInfo.estimatedDropoffDate;
          }
        }

        dropoffDate = DateUtil.adjustDateFromLocalToUTC(dropoffDate);

        // Derive service code from vehicle type
        let serviceLevelCode: string = ServiceLevelConstants.AMBULATORY_DOOR_TO_DOOR_CODE;

        if (state.selectedServiceLevelType === ServiceLevelType.NON_AMBULATORY) {
          serviceLevelCode = ServiceLevelConstants.NON_AMBULATORY_DOOR_TO_DOOR_CODE;
        }

        return this.riderService
          .listAvailableProviders(
            this.authenticatedRiderService.getLoggedInRiderId(),
            state.closestTransitProgram.id,
            pickupDate,
            dropoffDate,
            serviceLevelCode
          )
          .pipe(
            map(availableProviders =>
              RidesActions.fetchAvailableProvidersSuccess({
                availableProviders: availableProviders.sort((a, b) => a.name.localeCompare(b.name))
              })
            ),
            catchError(err => of(RidesActions.fetchAvailableProvidersFail(err)))
          );
      })
    );
  });

  setDefaultPaymentCollection$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          RidesActions.startNewItinerary,
          RidesActions.startNewItineraryLeg,
          RidesActions.startNewItineraryReturnTrip,
          RidesActions.startNewRideSubscription
        ),
        withLatestFrom(this.store$.select(ridesState)),
        filter(([action, state]) => !isNullOrUndefined(state.selectedOrganizationRiderListItem)),
        map(async ([action, state]) => {
          const isRiderPaymentRequired = state.selectedOrganizationRiderListItem.riderPaymentRequired;
          const isRiderPaymentAccountEnabled = state.selectedOrganizationRiderListItem.riderPaymentAccountEnabled;
          const isRiderFareCollectionEnabled = state.selectedOrganizationRiderListItem.riderFareCollectionEnabled;

          if (isRiderPaymentRequired) {
            const paymentAccounts = await firstValueFrom(this.paymentStore$.select(PaymentSelectors.getPaymentAccounts));

            if (isRiderFareCollectionEnabled && !isRiderPaymentAccountEnabled) {
              this.store$.dispatch(
                RidesActions.setPaymentCollectionDetailsForNewItinerary({
                  paymentCollectionTypeCode: PaymentCollectionConstants.FARE_COLLECTION,
                  paymentAccount: null
                })
              );
            } else if (isRiderPaymentAccountEnabled && !isRiderFareCollectionEnabled) {
              // If the state.selectedPaymentAccount is not null, reuse the selected account for new itinerary leg or return trip.
              let selectedPaymentAccount;

              if (!isNullOrUndefined(state.selectedPaymentAccount)) {
                selectedPaymentAccount = state.selectedPaymentAccount;
              } else {
                selectedPaymentAccount = await firstValueFrom(
                  this.paymentStore$.select(PaymentSelectors.getDefaultPaymentAccount).pipe(take(1))
                );
              }

              this.store$.dispatch(
                RidesActions.setPaymentCollectionDetailsForNewItinerary({
                  paymentCollectionTypeCode: PaymentCollectionConstants.PAYMENT_ACCOUNT,
                  paymentAccount: selectedPaymentAccount
                })
              );
            } else if (isRiderPaymentAccountEnabled && isRiderFareCollectionEnabled) {
              if (!isNullOrUndefined(state.selectedPaymentCollectionType)) {
                if (state.selectedPaymentCollectionType.code === PaymentCollectionConstants.FARE_COLLECTION) {
                  this.store$.dispatch(
                    RidesActions.setPaymentCollectionDetailsForNewItinerary({
                      paymentCollectionTypeCode: PaymentCollectionConstants.FARE_COLLECTION,
                      paymentAccount: null
                    })
                  );
                } else if (state.selectedPaymentCollectionType.code === PaymentCollectionConstants.PAYMENT_ACCOUNT) {
                  // If the state.selectedPaymentAccount is not null, reuse the selected account for new itinerary leg or return trip.
                  let selectedPaymentAccount: PaymentAccount;

                  if (!isNullOrUndefined(state.selectedPaymentAccount)) {
                    selectedPaymentAccount = state.selectedPaymentAccount;
                  } else {
                    selectedPaymentAccount = await firstValueFrom(
                      this.paymentStore$.select(PaymentSelectors.getDefaultPaymentAccount).pipe(take(1))
                    );
                  }

                  this.store$.dispatch(
                    RidesActions.setPaymentCollectionDetailsForNewItinerary({
                      paymentCollectionTypeCode: PaymentCollectionConstants.PAYMENT_ACCOUNT,
                      paymentAccount: selectedPaymentAccount
                    })
                  );
                }
              }
            }
          }
        })
      );
    },
    { dispatch: false }
  );

  setPaymentCollectionDetailsForNewItinerary = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.setPaymentCollectionDetailsForNewItinerary),
      withLatestFrom(this.store$.select(ridesState)),
      filter(([action, state]) => !isNullOrUndefined(state.selectedOrganizationRiderListItem)),
      switchMap(async ([action, state]) => {
        // If payment collection is fare collection and ride back up is disabled
        if (
          action.paymentCollectionTypeCode === PaymentCollectionConstants.FARE_COLLECTION &&
          state.currentRideRequestLeg?.rideBackupAccepted
        ) {
          // Clear payment info
          this.store$.dispatch(
            RidesActions.setPaymentCollectionDetailsForNewItinerarySuccess({ paymentCollectionTypeCode: null, paymentAccount: null })
          );

          return RidesActions.setPaymentCollectionDetailsForNewItineraryFail({
            error: new ApplicationException(
              this.translateService.instant('rideBackupPreference.error.rideBackupNotAvailableWithFareCollection.message')
            )
          });
        }

        return RidesActions.setPaymentCollectionDetailsForNewItinerarySuccess({
          paymentCollectionTypeCode: action.paymentCollectionTypeCode,
          paymentAccount: action.paymentAccount
        });
      })
    );
  });

  // TODO: This needs validation
  submitItinerary$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.submitItinerary),
      withLatestFrom(this.store$.select(ridesState)),
      filter(([action, state]) => !isNullOrUndefined(state.selectedOrganizationRiderListItem)),
      switchMap(([action, state]) => {
        // Ensuring we can't create a lyft fallback ride with fare collection
        if (
          state.selectedPaymentCollectionType?.code === PaymentCollectionConstants.FARE_COLLECTION &&
          state.currentRideRequestLeg?.rideBackupAccepted
        ) {
          // Clear payment info
          this.store$.dispatch(
            RidesActions.setPaymentCollectionDetailsForNewItinerarySuccess({ paymentCollectionTypeCode: null, paymentAccount: null })
          );

          return of(
            RidesActions.setPaymentCollectionDetailsForNewItineraryFail({
              error: new ApplicationException(
                this.translateService.instant('rideBackupPreference.error.rideBackupNotAvailableWithFareCollection.message')
              )
            })
          );
        }

        let itinerary: Itinerary = cloneDeep(state.itinerary);

        // Apply service level logic
        const currentRideRequestLeg: RideRequest = RideUtil.updateRideRequestServiceLevel(
          cloneDeep(state.currentRideRequestLeg),
          state.selectedServiceLevelType,
          state.selectedOrganizationRiderListItem?.organizationServiceLevels
        );

        const rideRequestInfo: RideRequestInfo = cloneDeep(state.currentRideRequestLeg.rideRequestInfo);
        const organizationRiderListItem: OrganizationRiderListItem = cloneDeep(state.selectedOrganizationRiderListItem);

        itinerary = RideUtil.processItineraryForSubmission(
          itinerary,
          currentRideRequestLeg,
          rideRequestInfo,
          organizationRiderListItem,
          this.authenticatedRiderService.getLoggedInRiderId()
        );

        if (state.itinerary.rideRequests.length === 0) {
          return from(
            this.loadingDialogService.presentLoadingDialog(this.translateService.instant('createRideOptionsOptions.loading.creatingItinerary'))
          ).pipe(
            switchMap(() => {
              return this.riderService.createItinerary(this.authenticatedRiderService.getLoggedInRiderId(), itinerary).pipe(
                switchMap(itinerary => {
                  this.loadingDialogService.dismissLoadingDialog();
                  return [
                    RidesActions.submitItinerarySuccess({ itinerary: itinerary }),
                    RidesActions.fetchRideDetail({ rideRequestId: itinerary.rideRequests[0].id })
                  ];
                }),
                catchError(err => of(RidesActions.submitItineraryFail({ error: err })))
              );
            })
          );
        } else {
          return this.riderService
            .addRideRequestToItinerary(this.authenticatedRiderService.getLoggedInRiderId(), itinerary.id, currentRideRequestLeg)
            .pipe(
              switchMap(rideRequest => {
                this.loadingDialogService.dismissLoadingDialog();
                return [
                  RidesActions.addRideRequestToItinerarySuccess({ rideRequest: rideRequest }),
                  RidesActions.fetchRideDetail({ rideRequestId: rideRequest.id })
                ];
              }),
              catchError(err => of(RidesActions.addRideRequestToItineraryFail({ error: err })))
            );
        }
      })
    );
  });

  submitRideSubscription = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.createRideSubscription),
      withLatestFrom(this.store$.select(ridesState)),
      switchMap(([action, state]) => {
        const createRideSubscriptionRequest: CreateRideSubscriptionRequest = new CreateRideSubscriptionRequest();
        createRideSubscriptionRequest.itinerary = this.itinerarySanitizer.sanitize(state.itinerary);
        createRideSubscriptionRequest.startDate = state.subscriptionStartDate;
        createRideSubscriptionRequest.endDate = state.subscriptionEndDate;
        createRideSubscriptionRequest.days = state.selectedDaysOfTheWeek.join();

        this.loadingDialogService.presentLoadingDialog(this.translateService.instant('dialog.loading.creatingRideSubscription'));
        return this.riderService
          .createRideSubscription(this.authenticatedRiderService.getLoggedInRiderId(), createRideSubscriptionRequest)
          .pipe(
            map(rideSubscriptionDetail => {
              return RidesActions.createRideSubscriptionSuccess({ rideSubscriptionDetail: rideSubscriptionDetail });
            }),
            catchError(err => of(RidesActions.createRideSubscriptionFail({ error: err }))),
            finalize(() => {
              this.loadingDialogService.dismissLoadingDialog();
            })
          );
      })
    );
  });

  activateWillCallError$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RidesActions.activateWillCallRideFail),
        map(action => {
          let message: string = action.error.message;

          if (RideErrorConstants.OVERLAPPING_RIDE_REQUEST.test(message)) {
            message = this.translateService.instant('ride.error.message.overlappingRideRequest');
            this.riderService
              .getTripDetailByRideRequestId(this.authenticatedRiderService.getLoggedInRiderId(), action.rideRequestId)
              .subscribe(
                rideDetail => {
                  this.loadingDialogService.dismissLoadingDialog();

                  this.modalService.presentAlertDialog({
                    message: message,
                    buttons: [
                      {
                        text: this.translateService.instant('support.button.contactSupport'),
                        handler: () => {
                          this.telephonyService.dialNumber(rideDetail.transitProgramInfo.riderCallCenterPhoneNumber);
                        }
                      },
                      {
                        text: this.translateService.instant('button.dismiss'),
                        role: 'cancel'
                      }
                    ]
                  });
                },
                err => {
                  this.loadingDialogService.dismissLoadingDialog();

                  // If we fail to get the ride detail call UZURV support
                  this.modalService.presentAlertDialog({
                    message: message,
                    buttons: [
                      {
                        text: this.translateService.instant('support.button.contactSupport'),
                        handler: () => {
                          this.telephonyService.dialNumber(this.appConfiguration.supportPhoneNumber);
                        }
                      },
                      {
                        text: this.translateService.instant('button.dismiss'),
                        role: 'cancel'
                      }
                    ]
                  });
                }
              );
          } else {
            this.loadingDialogService.dismissLoadingDialog();
            this.modalService.createNgrxErrorModalDialog(message);
          }
        })
      );
    },
    { dispatch: false }
  );

  handleFavoriteLocationError$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RidesActions.addFavoriteLocationFail),
        withLatestFrom(this.store$.select(ridesState)),
        map(([action, state]) => {
          this.loadingDialogService.dismissLoadingDialog();

          if (LocationErrorConstants.FAVORITE_LOCATION_ALREADY_EXISTS.test(action.error.message)) {
            const existingFavoriteLocation: FavoriteLocation = state.favoriteLocationListItems.find(
              favoriteLocation => favoriteLocation.location.id === action.favoriteLocation.location.id
            );

            this.modalService.presentAlertDialog({
              title: this.translateService.instant('favoriteLocationDetail.title.favoriteLocationAlreadyExists'),
              message: this.translateService.instant('favoriteLocationDetail.message.favoriteLocationAlreadyExists', {
                label: existingFavoriteLocation.label,
                streetAddress: existingFavoriteLocation.location.streetAddress1
              }),
              buttons: [
                {
                  text: this.translateService.instant('dialog.button.confirm'),
                  handler: () => {
                    this.store$.dispatch(
                      RidesActions.replaceFavoriteLocation({
                        toDelete: existingFavoriteLocation,
                        toCreate: action.favoriteLocation
                      })
                    );
                  }
                },
                {
                  text: this.translateService.instant('button.cancel'),
                  role: 'cancel'
                }
              ]
            });
          } else if (LocationErrorConstants.FAVORITE_LOCATION_LABEL_ALREADY_EXISTS.test(action.error.message)) {
            const existingFavoriteLocation: FavoriteLocation = state.favoriteLocationListItems.find(
              favoriteLocation => favoriteLocation.label === action.favoriteLocation.label
            );

            this.modalService.presentAlertDialog({
              title: this.translateService.instant('favoriteLocationDetail.title.favoriteLocationLabelAlreadyExists'),
              message: this.translateService.instant('favoriteLocationDetail.message.favoriteLocationLabelAlreadyExists', {
                label: existingFavoriteLocation.label,
                streetAddress: existingFavoriteLocation.location.streetAddress1
              }),
              buttons: [
                {
                  text: this.translateService.instant('dialog.button.confirm'),
                  handler: () => {
                    this.store$.dispatch(
                      RidesActions.replaceFavoriteLocation({
                        toDelete: existingFavoriteLocation,
                        toCreate: action.favoriteLocation
                      })
                    );
                  }
                },
                {
                  text: this.translateService.instant('button.cancel'),
                  role: 'cancel'
                }
              ]
            });
          } else {
            this.modalService.createNgrxErrorModalDialog(this.errorTranslationService.translateErrorMessage(action.error.message));
          }
        })
      );
    },
    { dispatch: false }
  );

  cancelRideError$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RidesActions.cancelRideFail),
        map(action => {
          if (RideErrorConstants.CANCELLATION_OUTSIDE_OF_CANCELLATION_WINDOW.test(action.error.message)) {
            this.displayCallUzurvToCancelRideAlert(
              action.riderCallCenterPhoneNumber,
              this.translateService.instant('rideDetails.error.outsideCancellationWindow')
            );
          } else {
            this.displayCallUzurvToCancelRideAlert(
              action.riderCallCenterPhoneNumber,
              this.translateService.instant('rideDetails.error.generic')
            );
          }
        })
      );
    },
    { dispatch: false }
  );

  fetchMatchingUnclaimedOrganizationRiders$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.fetchMatchingUnclaimedOrganizationRiders, RiderAuthenticationActions.riderAuthenticationComplete),
      withLatestFrom(this.store$.select(ridesState)),
      switchMap(([action]) => {
        return this.riderService.listMatchingUnclaimedOrganizationRiders(this.authenticatedRiderService.getLoggedInRiderId()).pipe(
          map(matchingUnclaimedOrganizationRiderListItems => {
            return RidesActions.fetchMatchingUnclaimedOrganizationRidersSuccess({
              matchingUnclaimedOrganizationRiderListItems: matchingUnclaimedOrganizationRiderListItems
            });
          }),
          catchError(err => of(RidesActions.fetchMatchingUnclaimedOrganizationRidersFail({ error: err })))
        );
      })
    );
  });

  claimOrganizationRider$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.claimOrganizationRider),
      withLatestFrom(this.store$.select(ridesState)),
      switchMap(([action]) => {
        // Build an object with the user's information to submit for proofing.
        const riderEligibilityProofRequest: RiderEligibilityProofRequest = new RiderEligibilityProofRequest();
        riderEligibilityProofRequest.firstName = action.organizationRider?.firstName;
        riderEligibilityProofRequest.lastName = action.organizationRider?.lastName;
        riderEligibilityProofRequest.dateOfBirth = action.organizationRider?.dateOfBirth;
        riderEligibilityProofRequest.phoneNumber = action.organizationRider?.phoneNumber;
        riderEligibilityProofRequest.partnerIdentifier = action.customerId;

        // Submit for proofing.
        return this.riderService
          .claimOrganizationRider(action.loggedInUserId, action.organizationRider.organizationRiderId, riderEligibilityProofRequest)
          .pipe(
            map(organizationRiderListItem => {
              return RidesActions.claimOrganizationRiderSuccess({
                organizationRiderListItem: organizationRiderListItem
              });
            }),
            catchError(err => of(RidesActions.claimOrganizationRiderFail({ error: err })))
          );
      })
    );
  });

  ignoreOrganizationRider$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.ignoreOrganizationRider),
      withLatestFrom(this.store$.select(ridesState)),
      switchMap(([action]) => {
        return this.riderService.ignoreOrganizationRider(action.loggedInUserId, action.organizationRiderId).pipe(
          map(() => {
            return RidesActions.ignoreOrganizationRiderSuccess({ organizationRiderId: action.organizationRiderId });
          }),
          catchError(err => of(RidesActions.ignoreOrganizationRiderFail({ error: err })))
        );
      })
    );
  });

  acceptRideBackup$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.acceptRideBackup),
      switchMap(action => {
        if (isNullOrUndefined(action.ride)) {
          return of(RidesActions.acceptRideBackupFail({ error: new ApplicationException('Ride missing') }));
        }

        const ac2cServiceLevel: OrganizationServiceLevel = action.ride.organizationInfo.organizationServiceLevels.find(
          orsl => orsl.serviceLevel.code === ServiceLevelConstants.AMBULATORY_CURB_TO_CURB_CODE
        );

        if (isNullOrUndefined(ac2cServiceLevel)) {
          return of(RidesActions.acceptRideBackupFail({ error: new ApplicationException('Missing required service level') }));
        }

        return this.riderService
          .patchRideRequest(this.authenticatedRiderService.getLoggedInRiderId(), action.ride.rideRequestId, {
            serviceLevel: ac2cServiceLevel.serviceLevel
          })
          .pipe(
            map(tripDetail => {
              return RidesActions.acceptRideBackupSuccess({ ride: tripDetail });
            }),
            catchError(err => of(RidesActions.acceptRideBackupFail({ error: err })))
          );
      })
    );
  });

  delayPickupTime$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.delayPickupTime),
      switchMap(action => {
        if (isNullOrUndefined(action.ride)) {
          return of(RidesActions.delayPickupTimeFail({ error: new ApplicationException('Ride missing') }));
        }

        if (isNullOrUndefined(action.pickUpDate)) {
          return of(RidesActions.delayPickupTimeFail({ error: new ApplicationException('Pick-up date missing') }));
        }

        if (action.ride.rideRequestType.onDemand) {
          return of(RidesActions.delayPickupTimeFail({ error: new ApplicationException('Cannot delay pick-up time for on demand ride') }));
        }

        this.loadingDialogService.presentLoadingDialog(this.translateService.instant('rideBackup.loading.delayingPickUp'));

        return this.riderService
          .delayRideRequestPickUpTime(this.authenticatedRiderService.getLoggedInRiderId(), action.ride.rideRequestId)
          .pipe(
            map(tripDetail => {
              this.loadingDialogService.dismissLoadingDialog();

              return RidesActions.delayPickupTimeSuccess({ ride: tripDetail });
            }),
            catchError(err => of(RidesActions.delayPickupTimeFail({ error: err })))
          );
      })
    );
  });

  submitRideReview$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.submitRideReview),
      switchMap(action => {
        return from(
          this.loadingDialogService.presentLoadingDialog(this.translateService.instant('driverRating.dialog.loading.submitting'))
        ).pipe(
          switchMap(() => {
            return this.riderService
              .submitDriverRating(this.authenticatedRiderService.getLoggedInRiderId(), action.reservationId, action.driverRating)
              .pipe(
                map(submittedRideReview => {
                  this.loadingDialogService.dismissLoadingDialog();
                  return RidesActions.submitRideReviewSuccess({ rideReview: submittedRideReview });
                }),
                catchError(err => of(RidesActions.submitRideReviewFail({ error: err })))
              );
          })
        );
      })
    );
  });

  displayErrorMessage$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          RidesActions.fetchUpcomingRideListItemsFail,
          RidesActions.fetchPastRideListItemsFail,
          RidesActions.fetchRideDetailFail,
          RidesActions.addLocationFail,
          RidesActions.fetchFavoriteLocationsFail,
          RidesActions.deleteFavoriteLocationFail,
          RidesActions.fetchRecentLocationsFail,
          RidesActions.submitItineraryFail,
          RidesActions.addRideRequestToItineraryFail,
          RidesActions.addOrganizationRiderServiceLevelFail,
          RidesActions.deleteOrganizationRiderServiceLevelFail,
          RidesActions.fetchRideRequestInfoFail,
          RidesActions.fetchClosestTransitProgramFail,
          RidesActions.fetchAvailableProvidersFail,
          RidesActions.createRideSubscriptionFail,
          RidesActions.fetchMatchingUnclaimedOrganizationRidersFail,
          RidesActions.ignoreOrganizationRiderFail,
          RidesActions.updateRideSubscriptionDetailsFail,
          RidesActions.setPaymentCollectionDetailsForNewItineraryFail,
          RidesActions.acceptRideBackupFail,
          RidesActions.submitRideReviewFail,
          RidesActions.updateRideRequestDriverNoteFail,
          RidesActions.removeRideRequestDriverNoteFail
        ),
        concatMap(action => from(this.presentErrorForFailedRideAction(action)))
      );
    },
    { dispatch: false }
  );

  processDelayPickUpTimeError$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RidesActions.delayPickupTimeFail),
        map(action => {
          this.loadingDialogService.dismissLoadingDialog();

          // Translate
          if (RideErrorConstants.OVERLAPPING_RIDE_REQUEST.test(action.error.message)) {
            this.modalService.presentAlertDialog({
              title: this.translateService.instant('rideBackup.error.title'),
              message: this.translateService.instant('rideBackup.error.message.overlappingRide')
            });
          } else if (RideErrorConstants.PICKUP_DATE_OUTSIDE_OF_OPERATING_HOURS.test(action.error.message)) {
            this.modalService.presentAlertDialog({
              title: this.translateService.instant('rideBackup.error.title'),
              message: this.translateService.instant('rideBackup.error.message.outsideOfOperatingHours')
            });
          } else if (RideErrorConstants.PICKUP_DATE_DOES_NOT_MATCH_ITINERARY_DATE.test(action.error.message)) {
            this.modalService.presentAlertDialog({
              title: this.translateService.instant('rideBackup.error.title'),
              message: this.translateService.instant('rideBackup.error.message.nextDay')
            });
          } else if (RideErrorConstants.PICKUP_DATE_IS_IN_PAST.test(action.error.message)) {
            this.modalService.presentAlertDialog({
              title: this.translateService.instant('rideBackup.error.title'),
              message: this.translateService.instant('rideBackup.error.message.pickUpTimeInPast')
            });
          } else if (RideErrorConstants.CANNOT_DELAY_PAIRED_RIDE.test(action.error.message)) {
            this.modalService.presentAlertDialog({
              title: this.translateService.instant('rideBackup.error.title'),
              message: this.translateService.instant('rideBackup.error.message.rideAlreadyPaired')
            });
          } else {
            this.modalService.presentAlertDialog({
              title: this.translateService.instant('rideBackup.error.title'),
              message: this.errorTranslationService.translateErrorMessage(action.error.message)
            });
          }
        })
      );
    },
    { dispatch: false }
  );

  displayIncorrectCustomerIdModal$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RidesActions.claimOrganizationRiderFail),
        map(action => {
          // If proofing is unsuccessful, present the user with the case specific error modal.
          if (RiderProfileErrorConstants.PROOFING_VALIDATION_FAILED.test(action.error.message)) {
            this.modalService.presentAlertDialog({
              alignTextCenter: true,
              title: this.translateService.instant('incorrectCustomerIdModal.title'),
              message: this.translateService.instant('incorrectCustomerIdModal.content.description'),
              buttons: [
                {
                  text: this.translateService.instant('button.ok'),
                  role: 'confirm'
                }
              ]
            });
          } else {
            this.modalService.createNgrxErrorModalDialog(this.errorTranslationService.translateErrorMessage(action.error.message));
          }
        })
      );
    },
    { dispatch: false }
  );

  updateRideRequestDriverNote$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.updateRideRequestDriverNote),
      withLatestFrom(this.store$.select(ridesState)),
      switchMap(([action, state]) => {
        return this.riderService
          .putRiderNote(this.authenticatedRiderService.getLoggedInRiderId(), state.selectedRide.rideRequestId, action.riderNote)
          .pipe(
            map(tripDetail => {
              return RidesActions.updateRideRequestDriverNoteSuccess({ ride: tripDetail });
            }),
            catchError(err => of(RidesActions.updateRideRequestDriverNoteFail({ error: err })))
          );
      })
    );
  });

  removeRideRequestDriverNote$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RidesActions.removeRideRequestDriverNote),
      withLatestFrom(this.store$.select(ridesState)),
      switchMap(([_action, state]) => {
        const riderId = this.authenticatedRiderService.getLoggedInRiderId();
        const rideRequestId = state.selectedRide.rideRequestId;
        return this.riderService.deleteRiderNote(riderId, rideRequestId).pipe(
          map(tripDetail => {
            return RidesActions.removeRideRequestDriverNoteSuccess({ ride: tripDetail });
          }),
          catchError(err => of(RidesActions.removeRideRequestDriverNoteFail({ error: err })))
        );
      })
    );
  });

  private async handleRideErrors(errorMessage: string) {
    if (RideErrorConstants.RIDE_LIMIT_EXCEEDED.test(errorMessage)) {
      await this.showRideErrorModal('rideError.rideLimitReached.message', 'rideError.rideLimitReached.title', true);
    } else if (RideErrorConstants.PICKUP_DATE_OUTSIDE_OF_OPERATING_HOURS.test(errorMessage)) {
      await this.showRideErrorModal('rideError.programOperatingHours.message', 'rideError.programOperatingHours.title', true);
    } else if (RideErrorConstants.OVERLAPPING_RIDE_REQUEST.test(errorMessage)) {
      await this.showRideErrorModal('rideError.rideSchedulingConflict.message', 'rideError.rideSchedulingConflict.title', false);
    } else if (RideErrorConstants.OUTSTANDING_BALANCE.test(errorMessage)) {
      await this.showRideErrorModal('rideError.outstandingBalance.message', 'rideError.outstandingBalance.title', false);
    } else if (RideErrorConstants.RIDE_REQUEST_OVERLAPS_RIDER_SUSPENSION.test(errorMessage)) {
      await this.showRideErrorModal('rideError.overlapsSuspension.message', 'rideError.overlapsSuspension.title', false);
    } else if (RideErrorConstants.LYFT_FALL_BACK_TRIP_NOTE_LIMIT_EXCEEDED.test(errorMessage)) {
      await this.showRideErrorModal(
        'rideError.lyftFallBackNoteLimitExceeded.message',
        'rideError.lyftFallBackNoteLimitExceeded.title',
        false
      );
    } else if (RideErrorConstants.ADDRESS_VALIDATION_FAILED.test(errorMessage)) {
      await this.showRideErrorModal('rideError.addressValidationFailed.message', 'rideError.addressValidationFailed.title', false);
    } else {
      this.modalService.createNgrxErrorModalDialog(this.errorTranslationService.translateErrorMessage(errorMessage));
    }
  }

  private async presentErrorForFailedRideAction(action: { type: string; error: Error }): Promise<void> {
    await this.loadingDialogService.dismissLoadingDialog();

    if (
      action.type === RidesActions.submitItineraryFail.type ||
      action.type === RidesActions.createRideSubscriptionFail.type ||
      action.type === RidesActions.addRideRequestToItineraryFail.type ||
      action.type === RidesActions.updateRideRequestDriverNoteFail.type ||
      action.type === RidesActions.addLocationFail.type
    ) {
      await this.handleRideErrors(action.error.message);
      return;
    }

    await this.modalService.createNgrxErrorModalDialog(this.errorTranslationService.translateErrorMessage(action.error.message));
  }

  private async showRideErrorModal(messageKey: string, titleKey: string, shouldShowCallSupportButton: boolean) {
    const errModal = await this.modalService.presentAlertDialog({
      message: this.translateService.instant(messageKey),
      title: this.translateService.instant(titleKey),
      buttons: shouldShowCallSupportButton
        ? [
          {
            text: this.translateService.instant('support.button.contactSupport'),
            handler: () => {
              this.telephonyService.dialNumber(this.appConfiguration.supportPhoneNumber);
            }
          },
          {
            text: this.translateService.instant('button.dismiss'),
            role: 'cancel'
          }
        ]
        : [
          {
            text: this.translateService.instant('button.dismiss'),
            role: 'cancel'
          }
        ]
    });

    await errModal.present();
  }

  private async displayCancelRideAlert(rideDetail: TripDetail) {
    // TODO(Ride WSS): Button interface needs to be refactored to match mat dialog
    await this.modalService.presentAlertDialog({
      message: this.translateService.instant('rideDetails.alert.cancel.message'),
      buttons: [
        {
          text: this.translateService.instant('button.no'),
          role: 'cancel',
          handler: () => {
            this.store$.dispatch(RidesActions.cancelRideDismissed());
          }
        },
        {
          text: this.translateService.instant('button.yesCancel'),
          role: 'danger',
          handler: () => {
            this.store$.dispatch(RidesActions.cancelRideConfirmed({ tripDetail: rideDetail }));
          }
        }
      ]
    });
  }

  private async displayCallUzurvToCancelRideAlert(riderCallCenterPhoneNumber: string, message: string) {
    // TODO(Ride WSS): Button interface needs to be refactored to match mat dialog
    await this.modalService.presentAlertDialog({
      message: message,
      buttons: [
        {
          text: this.translateService.instant('button.no'),
          role: 'cancel',
          handler: () => {
            this.store$.dispatch(RidesActions.cancelRideDismissed());
          }
        },
        {
          text: this.translateService.instant('support.button.contactSupport'),
          role: 'default',
          handler: () => {
            this.store$.dispatch(RidesActions.cancelRideDismissed());
            this.telephonyService.dialNumber(riderCallCenterPhoneNumber);
          }
        }
      ]
    });
  }

  private async displayCallOrganizationToCancelRideAlert(organizationName: string, bookingPhoneNumber: string) {
    await this.modalService.presentAlertDialog({
      title: this.translateService.instant('cancelRide.modal.title'),
      message: this.translateService.instant('cancelRide.modal.message', {
        organizationName: organizationName
      }),
      buttons: [
        {
          text: this.translateService.instant('button.callOrganization'),
          handler: () => {
            this.store$.dispatch(RidesActions.cancelRideDismissed());
            this.telephonyService.dialNumber(bookingPhoneNumber);
          }
        },
        {
          text: this.translateService.instant('button.close'),
          role: 'cancel',
          fill: 'outline',
          handler: () => {
            this.store$.dispatch(RidesActions.cancelRideDismissed());
          }
        }
      ]
    });
  }
}
